import { Injectable } from '@angular/core';
import { ThemeActionsService } from './theme-actions.service';
import { VIDEO_TARGET, VideoOverlayService } from '@shared/components/video-overlay/video-overlay.service';
import { LtoPricingService } from '../lto-pricing.service';
import { LaunchDarklyService } from '@core/services/launchdarkly.service';
import { Campaign, CAMPAIGN_ID, CampaignBanner, CampaignBannerItem, CONTENT_ID, DAYPART, isCombo } from '@core/models';
import { LocationService } from '../location.service';
import { ActivatedRoute } from '@angular/router';
import { LtoPricingFormatted, LtoPricingRequestCombo, LtoPricingRequestItem } from '@core/models/lto-pricing.model';
import { combineLatest, take } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class MinionsActionsService extends ThemeActionsService {
  private campaignId: string = CAMPAIGN_ID.national;
  private daypart: string = DAYPART.full_menu;
  private menu;
  private contentId = CONTENT_ID.marketing_banner;

  constructor(
    private route: ActivatedRoute,
    private videoOverlayService: VideoOverlayService,
    private ltoPricingService: LtoPricingService,
    private ldService: LaunchDarklyService,
    private locationService: LocationService,
  ) {
    super();
    this.setThemeId('minions');

    // Grab the daypart from either the route or the location service
    this.menu = this.route.snapshot.queryParamMap.get('menu');

    this.locationService.storeCurrentTime().then(({ menu }) => {
      this.daypart = this.menu || menu.toLowerCase();
    });
    this.campaignId = CAMPAIGN_ID.national;
  }

  /**
   * Play the minions theme video at the start of the conversation
   * as long as the price overlay fallback hasn't been triggered.
   */
  public override triggerConversationStartActions(callback: Function) {
    combineLatest([this.ldService.currentMenuContent(), this.ltoPricingService.pricingData$])
      .pipe(take(1))
      .subscribe((data: [Campaign[], LtoPricingFormatted | null]) => {
        const currentCampaigns = data[0];
        const currentLtoPricing = data[1];

        if (!this.isFallbackTriggered(currentCampaigns, currentLtoPricing)) {
          this.videoOverlayService.startVideoOverlay({
            src: './../../../../assets/videos/minions-final.webm',
            targetId: VIDEO_TARGET.Conversation,
            callback: callback,
            delayStartMs: 2500,
          });
        }
      });
  }

  // Helper function to get an item or combo's price
  public getItemPrice(
    id: string,
    type: 'combos' | 'items',
    pricingData: LtoPricingFormatted | null,
  ): number | undefined {
    if (pricingData) {
      const pricingObject = pricingData[type];
      if (pricingObject && pricingObject[id] !== undefined) {
        return pricingObject[id];
      }
    }
    return undefined;
  }

  // Wrapper function to get an item's price based on type
  public getBannerItemPOSInfo(
    bannerPOS: LtoPricingRequestItem | LtoPricingRequestCombo,
    pricingData: LtoPricingFormatted | null,
  ): number | undefined {
    if (isCombo(bannerPOS)) {
      return this.getItemPrice((bannerPOS as LtoPricingRequestCombo).promoId, 'combos', pricingData);
    } else {
      return this.getItemPrice((bannerPOS as LtoPricingRequestItem).posItemId, 'items', pricingData);
    }
  }

  // Helper function to determine whether the fallback for this menu has been triggered
  public isFallbackTriggered(menuCampaigns: Campaign[], pricingData: LtoPricingFormatted | null) {
    const menuContent = this.ltoPricingService.getCampaignContent(
      menuCampaigns,
      this.campaignId,
      this.daypart,
      this.contentId,
    );
    let fallback = false;
    menuContent?.banners.forEach((banner: CampaignBanner) => {
      banner.items.forEach((item: CampaignBannerItem) => {
        const posPrice = this.getBannerItemPOSInfo(item.pos, pricingData);
        if (posPrice === 0) {
          fallback = true;
        }
      });
    });

    return fallback;
  }
}
